/**
 * SecurityDesk — flagship security intelligence panel for the Yala Control Tower.
 *
 * Deep South situational awareness: ACLED incident data, AI digest, 90-day
 * timeline chart, live intel feed, and breakdown by type/province.
 *
 * Data sources: ACLED · Google News · Thai PBS · Prachatai
 */

import { useMemo, useState } from "react";
import type {
  ConflictIncident,
  ConflictEventType,
  SecurityStatus,
  FallbackTier,
  SecurityNewsItem,
  SecurityTimeline,
  SecurityDigest,
} from "@yala/shared";
import { PanelHeader } from "./PanelHeader";
import { useT } from "../hooks/useLang";
import {
  TimelineChart,
  IntelFeedItem,
  BreakdownBar,
  EVENT_BADGE,
  EVENT_COLOR,
  SEVERITY_LABEL,
  severityColor,
  trendArrow,
  trendColor,
} from "./SecurityDesk.helpers";

interface Props {
  incidents: ConflictIncident[];
  timeline: SecurityTimeline | null;
  digest: SecurityDigest | null;
  secNews: SecurityNewsItem[];
  status: SecurityStatus | null;
  loading: boolean;
  incidentsAge?: number | null;
  incidentsTier?: FallbackTier;
  feedAge?: number | null;
  feedTier?: FallbackTier;
}

const FEED_PAGE = 12;

export function SecurityDesk({
  incidents,
  timeline,
  digest,
  secNews,
  status,
  loading,
  incidentsAge,
  incidentsTier,
  feedAge,
}: Props) {
  const t = useT();
  const [showTh, setShowTh] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const totalIncidents = timeline?.totalIncidents ?? incidents.length;

  const totalFatalities = useMemo(
    () =>
      timeline?.totalFatalities ??
      incidents.reduce((s, i) => s + (Number.isFinite(i.fatalities) ? i.fatalities : 0), 0),
    [timeline, incidents],
  );

  const trend = timeline?.trend ?? "stable";

  const byProvince = useMemo<Record<string, number>>(() => {
    if (timeline?.byProvince) return timeline.byProvince;
    const map: Record<string, number> = {};
    for (const i of incidents) {
      const p = i.province ?? "Unknown";
      map[p] = (map[p] ?? 0) + 1;
    }
    return map;
  }, [timeline, incidents]);

  const byType = useMemo<Partial<Record<ConflictEventType, number>>>(() => {
    if (timeline?.byType) return timeline.byType;
    const map: Partial<Record<ConflictEventType, number>> = {};
    for (const i of incidents) {
      map[i.eventType] = (map[i.eventType] ?? 0) + 1;
    }
    return map;
  }, [timeline, incidents]);

  const feedItems = useMemo<SecurityNewsItem[]>(() => {
    if (secNews.length > 0) return secNews;
    return incidents
      .slice()
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map(
        (i): SecurityNewsItem => ({
          id: i.id,
          title: i.notes || `${i.eventType} — ${i.province}`,
          summaryEn: i.notes,
          source: i.source,
          sourceUrl: i.sourceUrl ?? "",
          publishedAt: i.date,
          eventType: i.eventType,
          province: i.province ?? null,
          severity: i.fatalities > 3 ? 4 : i.fatalities > 0 ? 3 : 2,
          lat: i.lat,
          lng: i.lng,
          placeName: i.district ?? null,
          tags: ["SEC"],
        }),
      );
  }, [secNews, incidents]);

  const visibleFeed = expanded ? feedItems : feedItems.slice(0, FEED_PAGE);

  const typeEntries = useMemo(
    () =>
      (Object.entries(byType) as [ConflictEventType, number][])
        .filter(([, n]) => n > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5),
    [byType],
  );

  const provinceEntries = useMemo(
    () =>
      Object.entries(byProvince)
        .filter(([, n]) => n > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5),
    [byProvince],
  );

  const maxType = typeEntries[0]?.[1] ?? 1;
  const maxProv = provinceEntries[0]?.[1] ?? 1;
  const bannerActive = !!(status?.martialLaw || status?.emergencyDecree);

  if (loading && incidents.length === 0 && !status) {
    return (
      <div className="col">
        <PanelHeader
          title={t("SECURITY INTELLIGENCE // DEEP SOUTH", "ข่าวกรองความมั่นคง // ชายแดนใต้")}
          source="acled · google news · thai pbs · prachatai"
        />
        <div className="skeleton" style={{ height: 40, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 60, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, width: "60%", marginTop: 8 }} />
      </div>
    );
  }

  return (
    <div className="col security-desk">
      {/* 1 — Header */}
      <PanelHeader
        title={t("SECURITY INTELLIGENCE // DEEP SOUTH", "ข่าวกรองความมั่นคง // ชายแดนใต้")}
        source="acled · google news · thai pbs · prachatai"
        ageMinutes={incidentsAge}
        fallbackTier={incidentsTier}
      />

      {/* 2 — Legal Status Banner */}
      <div
        style={{
          borderLeft: `2px solid ${bannerActive ? "var(--bad)" : "var(--rule)"}`,
          padding: "6px 10px",
          marginTop: 8,
          background: bannerActive ? "color-mix(in srgb, var(--bad) 8%, transparent)" : "transparent",
        }}
      >
        <div className="eyebrow mono" style={{ color: bannerActive ? "var(--bad)" : "var(--ink-low)" }}>
          {bannerActive ? "▲ SPECIAL SECURITY LAW ACTIVE" : "◆ LEGAL STATUS"}
        </div>
        <div className="mono" style={{ fontSize: "0.7rem", marginTop: 2 }}>
          {status?.label ?? "Emergency Decree & Martial Law active in most districts"}
        </div>
        <div className="eyebrow mono" style={{ color: "var(--ink-low)", marginTop: 2 }}>
          {status?.martialLaw ? "MARTIAL LAW" : "—"} · {status?.emergencyDecree ? "EMERGENCY DECREE" : "—"}
          {status?.verifiedAt ? ` · verified ${status.verifiedAt}` : ""}
        </div>
        <div className="eyebrow mono" style={{ color: "var(--ink-low)", marginTop: 2 }}>
          violence significantly lower since 2013 peak
        </div>
      </div>

      {/* 3 — Situation Digest */}
      <div style={{ marginTop: 8 }}>
        {digest ? (
          <div style={{ background: "var(--ground-soft)", padding: "8px 10px", borderRadius: 4 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span className="eyebrow mono">SITUATION ASSESSMENT</span>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <span className="eyebrow mono" style={{ color: severityColor(digest.situationLevel), fontSize: "0.6rem" }}>
                  {SEVERITY_LABEL[digest.situationLevel]}
                </span>
                <span className="eyebrow mono" style={{ color: "var(--ink-low)", fontSize: "0.58rem" }}>
                  {digest.source === "gemini" ? "AI · GEMINI" : "COMPUTED"}
                </span>
                <button
                  type="button"
                  onClick={() => setShowTh((p) => !p)}
                  className="mono"
                  style={{
                    fontSize: "0.58rem",
                    padding: "1px 4px",
                    border: "1px solid var(--rule)",
                    borderRadius: 2,
                    cursor: "pointer",
                    background: showTh ? "var(--rule)" : "transparent",
                    color: "var(--ink-mid)",
                  }}
                >
                  TH
                </button>
              </div>
            </div>
            <div style={{ fontSize: "0.7rem", lineHeight: 1.45, color: "var(--ink)" }}>
              {showTh ? digest.digestTh : digest.digestEn}
            </div>
          </div>
        ) : (
          <>
            <div className="eyebrow mono">SITUATION ASSESSMENT</div>
            <div className="skeleton" style={{ height: 12, marginTop: 6 }} />
            <div className="skeleton" style={{ height: 12, width: "80%", marginTop: 4 }} />
            <div className="skeleton" style={{ height: 12, width: "65%", marginTop: 4 }} />
          </>
        )}
      </div>

      {/* 4 — Key Metrics */}
      <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
        <div>
          <div className="eyebrow">INCIDENTS (90D)</div>
          <div className="mono" style={{ fontSize: "1.4rem", color: "var(--bad)", lineHeight: 1 }}>
            {totalIncidents}
          </div>
        </div>
        <div>
          <div className="eyebrow">FATALITIES (90D)</div>
          <div className="mono" style={{ fontSize: "1.4rem", color: "var(--bad)", lineHeight: 1 }}>
            {totalFatalities}
          </div>
        </div>
        <div>
          <div className="eyebrow">TREND</div>
          <div className="mono" style={{ fontSize: "1.2rem", color: trendColor(trend), lineHeight: 1 }}>
            {trendArrow(trend)}
          </div>
        </div>
      </div>

      {/* 5 — 90-day Timeline Chart */}
      {timeline && timeline.days.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="eyebrow mono" style={{ marginBottom: 3 }}>
            {timeline.windowDays}D INCIDENT TIMELINE
          </div>
          <TimelineChart days={timeline.days} />
          <div className="eyebrow mono" style={{ color: trendColor(trend), marginTop: 3 }}>
            {Math.abs(timeline.trendPct) < 1
              ? "◆ stable vs prior 30 days"
              : `${trendArrow(trend)} ${Math.abs(Math.round(timeline.trendPct))}% vs prior 30 days`}
          </div>
        </div>
      )}

      {/* 6 — Live Intel Feed */}
      <div style={{ marginTop: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="eyebrow mono">LIVE INTEL FEED</div>
          <div className="eyebrow mono" style={{ color: "var(--ink-low)" }}>
            {feedItems.length} items
          </div>
        </div>
        {visibleFeed.map((item, i) => (
          <IntelFeedItem key={item.id} item={item} index={i} />
        ))}
        {feedItems.length > FEED_PAGE && (
          <button
            type="button"
            onClick={() => setExpanded((p) => !p)}
            className="eyebrow mono"
            style={{
              marginTop: 6,
              color: "var(--ink-mid)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {expanded ? "▲ Show fewer" : `▼ Show ${feedItems.length - FEED_PAGE} more`}
          </button>
        )}
        {feedItems.length === 0 && (
          <div className="eyebrow mono" style={{ color: "var(--ink-low)", marginTop: 6 }}>
            No intel items available
          </div>
        )}
      </div>

      {/* 7 — Breakdown Grid */}
      <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
        <div style={{ flex: 1 }}>
          <div className="eyebrow mono" style={{ marginBottom: 4 }}>BY EVENT TYPE</div>
          {typeEntries.map(([type, count]) => (
            <BreakdownBar key={type} label={EVENT_BADGE[type]} count={count} max={maxType} color={EVENT_COLOR[type]} />
          ))}
          {typeEntries.length === 0 && <div className="eyebrow mono" style={{ color: "var(--ink-low)" }}>—</div>}
        </div>
        <div style={{ flex: 1 }}>
          <div className="eyebrow mono" style={{ marginBottom: 4 }}>BY PROVINCE</div>
          {provinceEntries.map(([prov, count]) => (
            <BreakdownBar key={prov} label={prov.toUpperCase().slice(0, 10)} count={count} max={maxProv} color="var(--bad)" />
          ))}
          {provinceEntries.length === 0 && <div className="eyebrow mono" style={{ color: "var(--ink-low)" }}>—</div>}
        </div>
      </div>

      {/* 8 — Source Health */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
        <span
          className="eyebrow mono"
          style={{
            fontSize: "0.58rem",
            padding: "1px 5px",
            border: "1px solid var(--rule)",
            borderRadius: 2,
            color: incidentsTier === "live" ? "var(--ok)" : incidentsTier === "scenario" ? "var(--warn)" : "var(--ink-low)",
          }}
        >
          ACLED {incidentsTier === "live" ? "✓ LIVE" : incidentsTier === "scenario" ? "◆ SCENARIO" : "◆ REF"}
        </span>
        <span
          className="eyebrow mono"
          style={{
            fontSize: "0.58rem",
            padding: "1px 5px",
            border: "1px solid var(--rule)",
            borderRadius: 2,
            color: feedAge == null ? "var(--ink-low)" : feedAge < 10 ? "var(--ok)" : feedAge < 60 ? "var(--warn)" : "var(--bad)",
          }}
        >
          NEWS {feedAge == null ? "—" : feedAge < 10 ? "✓ FRESH" : feedAge < 60 ? "◆ OK" : "✗ STALE"}
        </span>
        <span
          className="eyebrow mono"
          style={{ fontSize: "0.58rem", padding: "1px 5px", border: "1px solid var(--rule)", borderRadius: 2, color: "var(--ink-low)" }}
        >
          DSW ◆ REF
        </span>
        <span
          className="eyebrow mono"
          style={{ fontSize: "0.58rem", padding: "1px 5px", border: "1px solid var(--bad)", borderRadius: 2, color: "var(--bad)" }}
        >
          EMERGENCY DECREE ACTIVE
        </span>
      </div>
    </div>
  );
}
